import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import * as faceapi from "face-api.js";
import { apiClient } from "../services/api";

type CaptureStep = "loading" | "center" | "up" | "down" | "left" | "right" | "smile" | "processing" | "success" | "error";

function FaceRegisterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  const [currentStep, setCurrentStep] = useState<CaptureStep>("loading");
  const [message, setMessage] = useState("모델을 불러오는 중...");
  const [capturedDescriptors, setCapturedDescriptors] = useState<Float32Array[]>([]);
  const [streamStarted, setStreamStarted] = useState(false);
  
  // URL에서 studentNumber 가져오기
  const studentNumber = new URLSearchParams(location.search).get("studentNumber") || "";

  // face-api.js 모델 로드
  useEffect(() => {
    const loadModels = async () => {
      try {
        const MODEL_URL = "/models"; // public/models 폴더에 모델 파일 필요
        await faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL);
        await faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL);
        await faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL);
        
        setCurrentStep("center");
        setMessage("정면을 바라봐주세요");
        startCamera();
      } catch (error) {
        console.error("모델 로드 실패:", error);
        setCurrentStep("error");
        setMessage("얼굴 인식 모델 로드에 실패했습니다. 모델 파일을 확인해주세요.");
      }
    };

    loadModels();

    return () => {
      stopCamera();
    };
    // eslint-disable-next-line
  }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480, facingMode: "user" }
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        
        // 비디오 메타데이터가 로드된 후 재생
        videoRef.current.onloadedmetadata = () => {
          if (videoRef.current) {
            videoRef.current.play().catch(err => {
              console.error("비디오 재생 실패:", err);
            });
            setStreamStarted(true);
          }
        };
      }
    } catch (error) {
      console.error("카메라 접근 실패:", error);
      setCurrentStep("error");
      setMessage("카메라에 접근할 수 없습니다. 권한을 확인해주세요.");
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
    }
  };

  const captureDescriptor = async () => {
    if (!videoRef.current || !streamStarted) return null;

    try {
      const detections = await faceapi
        .detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions())
        .withFaceLandmarks()
        .withFaceDescriptor();

      if (detections) {
        // 캔버스에 감지된 얼굴 표시
        if (canvasRef.current && videoRef.current) {
          const displaySize = {
            width: videoRef.current.videoWidth,
            height: videoRef.current.videoHeight
          };
          faceapi.matchDimensions(canvasRef.current, displaySize);
          const resizedDetections = faceapi.resizeResults(detections, displaySize);
          const ctx = canvasRef.current.getContext("2d");
          if (ctx) {
            ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
            faceapi.draw.drawDetections(canvasRef.current, resizedDetections);
            faceapi.draw.drawFaceLandmarks(canvasRef.current, resizedDetections);
          }
        }
        
        return detections.descriptor;
      }
      return null;
    } catch (error) {
      console.error("얼굴 감지 실패:", error);
      return null;
    }
  };

  const handleCapture = async () => {
    setMessage("얼굴을 감지하는 중...");
    
    const descriptor = await captureDescriptor();
    
    if (!descriptor) {
      setMessage("얼굴을 감지할 수 없습니다. 다시 시도해주세요.");
      setTimeout(() => {
        setMessage(getStepMessage(currentStep));
      }, 2000);
      return;
    }

    const newDescriptors = [...capturedDescriptors, descriptor];
    setCapturedDescriptors(newDescriptors);

    // 다음 단계로 진행
    switch (currentStep) {
      case "center":
        setCurrentStep("up");
        setMessage("위를 바라봐주세요");
        break;
      case "up":
        setCurrentStep("down");
        setMessage("아래를 바라봐주세요");
        break;
      case "down":
        setCurrentStep("left");
        setMessage("왼쪽을 바라봐주세요");
        break;
      case "left":
        setCurrentStep("right");
        setMessage("오른쪽을 바라봐주세요");
        break;
      case "right":
        setCurrentStep("smile");
        setMessage("웃으면서 정면을 봐주세요 😊");
        break;
      case "smile":
        setCurrentStep("processing");
        setMessage("얼굴 정보를 저장하는 중...");
        await saveFaceDescriptors(newDescriptors);
        break;
      default:
        break;
    }
  };

  const saveFaceDescriptors = async (descriptors: Float32Array[]) => {
    try {
      // 여러 descriptor의 평균값 계산
      const avgDescriptor = new Float32Array(128);
      for (let i = 0; i < 128; i++) {
        avgDescriptor[i] = descriptors.reduce((sum, desc) => sum + desc[i], 0) / descriptors.length;
      }

      // API로 저장 (studentNumber와 함께)
      await apiClient.saveFaceDescriptor(studentNumber, Array.from(avgDescriptor));
      
      setCurrentStep("success");
      setMessage("얼굴 등록이 완료되었습니다!");
      
      setTimeout(() => {
        stopCamera();
        navigate("/viewer");
      }, 2000);
    } catch (error) {
      console.error("얼굴 정보 저장 실패:", error);
      setCurrentStep("error");
      setMessage("얼굴 정보 저장에 실패했습니다. 다시 시도해주세요.");
    }
  };

  const getStepMessage = (step: CaptureStep): string => {
    switch (step) {
      case "loading": return "모델을 불러오는 중...";
      case "center": return "정면을 바라봐주세요";
      case "up": return "위를 바라봐주세요";
      case "down": return "아래를 바라봐주세요";
      case "left": return "왼쪽을 바라봐주세요";
      case "right": return "오른쪽을 바라봐주세요";
      case "smile": return "웃으면서 정면을 봐주세요 😊";
      case "processing": return "얼굴 정보를 저장하는 중...";
      case "success": return "얼굴 등록이 완료되었습니다!";
      case "error": return "오류가 발생했습니다";
      default: return "";
    }
  };

  const handleCancel = () => {
    stopCamera();
    navigate("/viewer");
  };

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      background: "#1a1a2e",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden"
    }}>
      <h1 style={{ color: "#ffffff", marginBottom: "20px", fontSize: "32px" }}>
        얼굴 등록
      </h1>
      
      <div style={{
        color: "#ffffff",
        fontSize: "20px",
        marginBottom: "20px",
        textAlign: "center"
      }}>
        출결번호: <strong>{studentNumber}</strong>
      </div>

      <div style={{
        position: "relative",
        width: "640px",
        height: "480px",
        marginBottom: "20px",
        borderRadius: "12px",
        overflow: "hidden",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)"
      }}>
        <video
          ref={videoRef}
          width="640"
          height="480"
          style={{ display: "block", transform: "scaleX(-1)" }}
        />
        <canvas
          ref={canvasRef}
          width="640"
          height="480"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            transform: "scaleX(-1)"
          }}
        />
      </div>

      <div style={{
        color: "#ffffff",
        fontSize: "24px",
        marginBottom: "20px",
        minHeight: "32px",
        textAlign: "center"
      }}>
        {message}
      </div>

      <div style={{
        display: "flex",
        gap: "20px",
        marginBottom: "20px"
      }}>
        {["center", "up", "down", "left", "right", "smile"].map((step, idx) => (
          <div
            key={step}
            style={{
              width: "60px",
              height: "60px",
              borderRadius: "50%",
              background: capturedDescriptors.length > idx ? "#10b981" : "#374151",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#ffffff",
              fontSize: "20px",
              fontWeight: "bold",
              transition: "all 0.3s"
            }}
          >
            {capturedDescriptors.length > idx ? "✓" : idx + 1}
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: "16px" }}>
        {!["loading", "processing", "success"].includes(currentStep) && (
          <button
            onClick={handleCapture}
            disabled={currentStep === "error"}
            style={{
              width: "200px",
              height: "60px",
              fontSize: "18px",
              fontWeight: "bold",
              borderRadius: "12px",
              border: "none",
              background: currentStep === "error" ? "#6b7280" : "#8b5cf6",
              color: "#ffffff",
              cursor: currentStep === "error" ? "not-allowed" : "pointer",
              transition: "all 0.2s",
              boxShadow: "0 4px 12px rgba(139, 92, 246, 0.3)"
            }}
          >
            📸 촬영
          </button>
        )}
        
        <button
          onClick={handleCancel}
          style={{
            width: "200px",
            height: "60px",
            fontSize: "18px",
            fontWeight: "bold",
            borderRadius: "12px",
            border: "2px solid #ef4444",
            background: "transparent",
            color: "#ef4444",
            cursor: "pointer",
            transition: "all 0.2s"
          }}
        >
          취소
        </button>
      </div>
    </div>
  );
}

export default FaceRegisterPage;
